import { playShutterSound } from './shutter-sound';

/**
 * Camera-shutter page flip: focus frame locks → aperture closes to a point →
 * page jumps (while fully black) → aperture opens. Single overlay, single in-flight run.
 */
const FRAME_IN = 400;
const LOCK_HOLD = 220;
const DEFOCUS_PX = 9;
const REFOCUS_MS = 680;
const CLOSE = 360;
const BLACK_HOLD = 120;
const OPEN = 440;

let overlay: HTMLDivElement | null = null;
let iris: HTMLDivElement | null = null;
let frame: HTMLDivElement | null = null;
let running = false;

const easeIn = (t: number) => t * t * t;
const easeOut = (t: number) => 1 - (1 - t) ** 3;
const wait = (ms: number) => new Promise<void>((r) => { window.setTimeout(r, ms); });

function ensureDom() {
  if (overlay) return;
  overlay = document.createElement('div');
  overlay.className = 'shutter-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  iris = document.createElement('div');
  iris.className = 'shutter-iris';
  frame = document.createElement('div');
  frame.className = 'shutter-frame';
  ['tl', 'tr', 'bl', 'br'].forEach((c) => {
    const s = document.createElement('span');
    s.className = `shutter-frame__c shutter-frame__c--${c}`;
    frame!.appendChild(s);
  });
  const dot = document.createElement('span');
  dot.className = 'shutter-frame__dot';
  frame.appendChild(dot);
  overlay.append(iris, frame);
  document.body.appendChild(overlay);
}

/**
 * Blur via backdrop-filter on the overlay. Never put `filter` on #root: it turns #root into
 * the containing block for position:fixed children, which makes the fixed Hero layer
 * stretch to full document height (blank screen + layout jump).
 */
function setBlur(px: number) {
  if (!overlay) return;
  const v = px > 0.05 ? `blur(${px.toFixed(2)}px)` : '';
  overlay.style.backdropFilter = v;
  overlay.style.setProperty('-webkit-backdrop-filter', v);
}

function setRadius(r: number) {
  iris!.style.setProperty('--r', `${r}px`);
}

function tween(ms: number, from: number, to: number, ease: (t: number) => number, fn: (v: number) => void) {
  return new Promise<void>((resolve) => {
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      fn(from + (to - from) * ease(t));
      if (t < 1) requestAnimationFrame(step); else resolve();
    };
    requestAnimationFrame(step);
  });
}

export function isShutterRunning() { return running; }

const SETTLE_MAX = 700;
const nextFrame = () => new Promise<void>((r) => { requestAnimationFrame(() => r()); });

/** While black: decode on-screen images of the new view, then wait two stable frames. */
async function settleNewView() {
  const vh = window.innerHeight;
  const imgs = Array.from(document.querySelectorAll('img')).filter((img) => {
    const r = img.getBoundingClientRect();
    return r.width > 0 && r.bottom > 0 && r.top < vh;
  });
  const decodes = Promise.all(imgs.map((img) => img.decode().catch(() => undefined)));
  await Promise.race([decodes, wait(SETTLE_MAX)]);
  await nextFrame();
  await nextFrame();
}

/** Runs the shutter and calls `jump` while the screen is fully closed. */
export async function runShutter(jump: () => void): Promise<void> {
  if (running) return;
  running = true;
  ensureDom();
  const el = overlay!;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.classList.add('is-active');

  try {
    if (reduced) {
      const ir = iris!;
      ir.style.opacity = '0';
      el.classList.add('is-reduced');
      void ir.offsetWidth;
      ir.style.opacity = '1';
      await wait(170);
      jump();
      await wait(40);
      ir.style.opacity = '0';
      await wait(170);
      el.classList.remove('is-reduced');
      ir.style.opacity = '';
      return;
    }

    const maxR = Math.hypot(window.innerWidth, window.innerHeight) / 2 + 4;
    setRadius(maxR);
    frame!.classList.remove('is-locked', 'is-out');
    void frame!.offsetWidth;
    frame!.classList.add('is-in');
    // Focus hunting: the scene drifts out of focus while the frame searches…
    await tween(FRAME_IN, 0, DEFOCUS_PX, easeOut, setBlur);
    frame!.classList.add('is-locked');
    // …then snaps sharp as focus locks.
    await tween(LOCK_HOLD, DEFOCUS_PX, 0, easeOut, setBlur);

    playShutterSound();
    frame!.classList.add('is-out');
    await tween(CLOSE, maxR, 0, easeIn, setRadius);
    setBlur(DEFOCUS_PX + 3);
    jump();
    await Promise.all([wait(BLACK_HOLD), settleNewView()]);
    // New page opens soft and pulls into focus behind the opening aperture.
    const refocus = tween(REFOCUS_MS, DEFOCUS_PX + 3, 0, easeOut, setBlur);
    await tween(OPEN, 0, maxR, easeOut, setRadius);
    await refocus;
  } finally {
    setBlur(0);
    frame!.classList.remove('is-in', 'is-locked', 'is-out');
    el.classList.remove('is-active');
    running = false;
  }
}

/* ---- Nav bridge: the home page registers a handler so nav links can use the shutter. ---- */
type NavHandler = (hash: string) => boolean;
let navHandler: NavHandler | null = null;

export function registerShutterNav(fn: NavHandler | null) { navHandler = fn; }

/** Returns true when the shutter handled the navigation. */
export function shutterNavigate(hash: string): boolean {
  return navHandler ? navHandler(hash) : false;
}